import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AppRoutes, FundService } from '@gs-2.0/core';
import { FundDisplay, Step } from '@gs-2.0/shared';
import { TransactionService } from '../../../_core/api/transaction.service';
import FundTypes from '../../../_core/constants/fund-types';
import PageTitles from '../../../_core/constants/page-titles';
import { SERVER_ERROR } from '../../../_core/constants/ServerError';
import Urls from '../../../_core/constants/urls';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import { GrantDataFee, GrantTransferData } from '../../../_core/models/grantData';
import { Fund } from '../../../_core/models/fund';
import { GlobalConfigApiService } from '../../../_core/api/global-config.service';
import { TitleService } from '../../../_core/services/title.service';
import { FundService as PortalFundService } from '../../../_core/services/fund.service';
import { FeatureFlagService } from '../../../_core/services/feature-flag.service';
import { FeatureFlagKeys } from '../../../_core/models/FeatureFlags';

@Component({
  selector: 'app-distribution',
  templateUrl: './distribution.component.html',
  styleUrls: ['./distribution.component.scss'],
  standalone: false,
})
export class DistributionComponent implements OnInit, OnDestroy {
  urls = Urls;
  currentFund: Fund;
  grantFormData: any;
  grantFormValid = false;
  grantDataFee: GrantDataFee;

  grantDisplayData = new GrantTransferData();

  stepperSteps: Step[] = [
    { label: 'contribute.step.details', number: 1 },
    { label: 'contribute.step.summary', number: 2 },
    { label: 'contribute.step.confirmation', number: 3 },
  ];

  currentStep = 1;
  showError = false;
  isMinAmountError = false;
  loading = false;
  pageLoading = false;
  fundTypes = FundTypes;

  currencyMismatchErrorMsg: string | null = null;
  currencySymbol = '';
  currencyCode = '';
  grantingCurrencyBalance: number | null = null;
  formDisabled = false;
  validating = false;
  recurringEnabled = false;

  numberFormatConfig = { decimalSymbol: '.', digitGroupingSymbol: ',', digitGrouping: '123,456,789' };

  private fundSubscription: Subscription;
  private recurringSubscription: Subscription;

  get activeStepIndex(): number {
    return this.currentStep - 1;
  }

  onFundChanged(fund: FundDisplay): void {
    this.fundService.setSelectedFund(fund.id);
    this.router.navigate([AppRoutes.FUND_DASHBOARD]);
  }

  private triggerFundChanged(): void {
    if (!this.currentFund) {
      this.portalFundService.fundChanged(null);
      return;
    }
    this.portalFundService.fundChanged(this.currentFund);
  }

  constructor(
    private router: Router,
    private titleService: TitleService,
    protected fundService: FundService,
    private portalFundService: PortalFundService,
    private transactionService: TransactionService,
    private featureFlagService: FeatureFlagService,
    private globalConfigApiService: GlobalConfigApiService
  ) {}

  ngOnInit(): void {
    this.loadNumberFormat();
    this.recurringSubscription = this.featureFlagService.hasFeatureFlag(FeatureFlagKeys.RecurringDistributions).subscribe((enabled) => {
      this.recurringEnabled = enabled;
    });

    this.fundSubscription = this.portalFundService.fund$.subscribe((fund) => {
      this.fundService.parentFundExpired.set(fund?.parentFund?.expired ?? false);
    });

    const fund = this.fundService.selectedFund();
    this.currentFund = fund as unknown as Fund;
    if (!this.currentFund) {
      this.pageLoading = true;
      this.fundService.loadFunds().then(() => {
        this.currentFund = this.fundService.selectedFund() as unknown as Fund;
        this.pageLoading = false;
        this.setPageTitle();
        this.triggerFundChanged();
        this.checkForCurrencyMismatch();
      });
    } else {
      this.setPageTitle();
      this.triggerFundChanged();
      this.checkForCurrencyMismatch();
    }
  }

  private loadNumberFormat(): void {
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config: any) => {
        if (config?.response?.numberFormat) {
          this.numberFormatConfig = {
            decimalSymbol: config.response.numberFormat.decimalSymbol?.value || '.',
            digitGroupingSymbol: config.response.numberFormat.digitGroupingSymbol?.value || ',',
            digitGrouping: config.response.numberFormat.digitGrouping || '123,456,789',
          };
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.fundSubscription?.unsubscribe();
    this.recurringSubscription?.unsubscribe();
  }

  setPageTitle(): void {
    if (!this.currentFund) return;
    this.currentFund.fundType === FundTypes.CHARITY
      ? this.titleService.titleChanged(PageTitles.GRANT_CHARITY)
      : this.titleService.titleChanged(PageTitles.GRANT_DAF);
  }

  checkForCurrencyMismatch(): void {
    if (!this.currentFund || this.currentFund.fundType !== FundTypes.CHARITY) return;

    if (this.currentFund.currency !== this.currentFund.receiveGrantCurrency) {
      this.currencySymbol = this.currentFund.receiveGrantCurrencySymbol;
      this.currencyCode = this.currentFund.receiveGrantCurrency;
      this.validating = true;

      this.transactionService.getCurrencies(this.currentFund.cseg5).subscribe({
        next: (currencies: any[]) => {
          const grantingCurrency = currencies.find((c: any) => c.id === this.currentFund.receiveGrantCurrency) || {
            availableAmountFundCash: 0,
          };
          const availableAmount = grantingCurrency.availableAmountFundCash;
          this.grantingCurrencyBalance = availableAmount;

          if (availableAmount <= 0) {
            // No balance in granting currency
            this.currencyMismatchErrorMsg = 'distribution.warning.currency_not_allowed';
            this.formDisabled = true;
            this.validating = false;
            return;
          }

          // Has balance — verify minimum via calculateGrant
          this.transactionService.calculateGrant({
            amount: availableAmount,
            currency: this.currentFund.receiveGrantCurrency,
            senderFundId: this.currentFund.cseg5,
          }).subscribe({
            next: () => {
              this.validating = false;
            },
            error: (error: any) => {
              if (error?.status === 409 && error?.error?.minimumGrantAmount) {
                this.currencyMismatchErrorMsg = 'distribution.warning.insufficient_balance';
              }
              this.formDisabled = true;
              this.validating = false;
            },
          });
        },
        error: () => {
          this.validating = false;
        },
      });
    }
  }

  computeDisplayData(): void {
    this.grantDisplayData = { ...this.grantFormData };
    this.grantDisplayData.privacy = this.grantFormData.privacy ? 'Yes' : 'No';
    this.grantDisplayData.isRecurring = this.grantFormData.timing === 'recurring';
    this.grantDisplayData.timing =
      this.grantFormData.timing === 'specificDate'
        ? this.grantFormData.date
        : 'As soon as possible';
  }

  goToSummary(): void {
    if (this.grantFormValid) {
      window.scrollTo(0, 0);
      this.computeDisplayData();
      this.currentStep = 2;
    }
  }

  goToForm(): void {
    window.scrollTo(0, 0);
    this.currentStep = 1;
    this.showError = false;
    this.isMinAmountError = false;
  }

  onSubmit(): void {
    this.loading = true;
    const payload = this.buildDistributionPayload(this.grantFormData, this.currentFund);
    this.transactionService.submitTransactionFundToFund(payload).subscribe({
      next: () => {
        // Distribution changed fund balance — refresh so the dashboard's
        // next visit reflects the post-distribution state instead of the
        // F2-cached pre-distribution balances.
        this.fundService.loadFunds({ force: true });
        this.transactionService.getSummaryAndCurrencies({ cseg5: this.currentFund.cseg5 }).subscribe();
        this.currentStep = 3;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        if (error.status === 409 && error.error?.message === SERVER_ERROR.NOT_MINIMUM_AMOUNT) {
          this.isMinAmountError = true;
        } else {
          this.showError = true;
        }
      },
    });
  }

  goToPending(): void {
    this.router.navigate([`${Urls.APP}/${Urls.ACTIVITY}/${Urls.PENDING}`]);
  }

  goToDashboard(): void {
    this.router.navigate([`${Urls.FUND}/${Urls.DASHBOARD}`]);
  }

  reloadDistribution(): void {
    const currentUrl = this.router.url;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([currentUrl]);
  }

  private buildDistributionPayload(formData: any, fund: Fund): any {
    const isRecurring = formData.timing === 'recurring';
    return {
      isFromGrantsTab: false,
      senderCseg5: fund.cseg5,
      senderFundName: fund.fundName,
      receiverCseg5: formData.receiverCseg5,
      receiverFundName: formData.receiverFundName,
      currency: this.currencyCode || fund.currency,
      amount: formData.amount,
      date: SharedMethodsHelpers.dateToMilliseconds(formData.date, true),
      privacy: formData.privacy ? formData.privacy : false,
      charityInstruction: formData.charityInstruction ? formData.charityInstruction : '',
      additionalNotes: formData.additionalNotes,
      charityProject: fund.fundType === FundTypes.CHARITY || fund.fundType === FundTypes.PROJECT,
      fundType: fund.fundType,
      amountGrantAssessments: this.grantDataFee?.calculatedGrantAssessmentExpense ?? 0,
      amountProcessingAssessments: this.grantDataFee?.processingAssessmentExpense ?? 0,
      amountSupGrantAssessments: this.grantDataFee?.supplementalGrantAssessment ?? 0,
      isRecurring,
      recurrencePattern: isRecurring ? (formData.recurrencePattern ?? null) : null,
      startDate: isRecurring ? (formData.startDate ?? null) : null,
      endDate: isRecurring ? (formData.endDate ?? null) : null,
      timeZone: this.getTimezone(),
    };
  }

  private getTimezone(): string {
    const offset = -new Date().getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
    const mins = (Math.abs(offset) % 60).toString().padStart(2, '0');
    return `UTC${sign}${hours}:${mins}`;
  }
}
